// shell/components/entity-fields/form-renderers/entity-search-input.ts

import { createElement, clearElement } from '../../../utils/dom';
import { debounce } from '../../../utils/dom';
import type { EntitySearchFormField, SelectOption } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders an entity search input with async options.
 */
export function renderEntitySearchInput<T>(
    field: EntitySearchFormField<T>,
    initialValue: unknown,
    onChange: (value: unknown) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--entity-search',
    });

    // Current selection(s)
    let selectedItems: SelectOption[] = [];
    if (initialValue != null) {
        if (Array.isArray(initialValue)) {
            selectedItems = initialValue as SelectOption[];
        } else {
            selectedItems = [initialValue as SelectOption];
        }
    }

    // Input element
    const inputEl = createElement('input', {
        className: 'ev-input ev-search-input',
        attributes: {
            type: 'text',
            ...(field.placeholder ? { placeholder: field.placeholder } : {}),
            ...(field.disabled ? { disabled: '' } : {}),
            'aria-autocomplete': 'list',
            'aria-expanded': 'false',
        },
    }) as HTMLInputElement;

    // Dropdown for results
    const dropdown = createElement('div', {
        className: 'ev-search-dropdown',
        attributes: { role: 'listbox' },
    });
    dropdown.style.display = 'none';

    // Selected items display
    const selectedContainer = createElement('div', {
        className: 'ev-selected-items',
    });

    // State
    let isOpen = false;
    let options: SelectOption[] = [];
    let highlightIndex = -1;
    let abortController: AbortController | null = null;

    // Render selected items
    const renderSelected = () => {
        clearElement(selectedContainer);
        for (const item of selectedItems) {
            const tag = createElement('span', {
                className: 'ev-selected-tag',
            });
            const label = createElement('span', {
                className: 'ev-selected-label',
                textContent: item.label,
            });
            tag.appendChild(label);

            if (!field.disabled) {
                const removeBtn = createElement('button', {
                    className: 'ev-selected-remove',
                    textContent: '×',
                    attributes: { type: 'button', 'aria-label': `Remove ${item.label}` },
                    onClick: (e) => {
                        e.stopPropagation();
                        selectedItems = selectedItems.filter(i => i.key !== item.key);
                        renderSelected();
                        emitChange();
                    },
                });
                tag.appendChild(removeBtn);
            }
            selectedContainer.appendChild(tag);
        }
    };

    // Emit change
    const emitChange = () => {
        if (field.multiple) {
            onChange(selectedItems);
        } else {
            onChange(selectedItems.length > 0 ? selectedItems[0] : null);
        }
    };

    // Fetch options
    const fetchOptions = async (query: string) => {
        // Cancel previous request
        abortController?.abort();
        abortController = new AbortController();

        try {
            dropdown.classList.add('ev-search-dropdown--loading');
            options = await field.fetchOptions(query);
            renderDropdown();
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                console.error('Error fetching options:', error);
                options = [];
                renderDropdown();
            }
        } finally {
            dropdown.classList.remove('ev-search-dropdown--loading');
        }
    };

    const debouncedFetch = debounce((query: unknown) => fetchOptions(query as string), 300);

    // Render dropdown
    const renderDropdown = () => {
        clearElement(dropdown);
        highlightIndex = -1;

        if (options.length === 0) {
            const empty = createElement('div', {
                className: 'ev-search-empty',
                textContent: 'No results found',
            });
            dropdown.appendChild(empty);
            return;
        }

        const selectedKeys = new Set(selectedItems.map(i => i.key));

        options.forEach((option, index) => {
            const isSelected = selectedKeys.has(option.key);
            const item = createElement('div', {
                className: `ev-search-option ${isSelected ? 'ev-search-option--selected' : ''}`,
                textContent: option.label,
                attributes: {
                    role: 'option',
                    'aria-selected': isSelected ? 'true' : 'false',
                    'data-index': String(index),
                },
                onClick: () => selectOption(option),
            });
            dropdown.appendChild(item);
        });
    };

    // Select an option
    const selectOption = (option: SelectOption) => {
        if (field.multiple) {
            const exists = selectedItems.some(i => i.key === option.key);
            if (!exists) {
                selectedItems = [...selectedItems, option];
            }
        } else {
            selectedItems = [option];
            closeDropdown();
        }
        renderSelected();
        emitChange();
        inputEl.value = '';
    };

    // Open/close dropdown
    const openDropdown = () => {
        isOpen = true;
        dropdown.style.display = 'block';
        inputEl.setAttribute('aria-expanded', 'true');
    };

    const closeDropdown = () => {
        isOpen = false;
        dropdown.style.display = 'none';
        inputEl.setAttribute('aria-expanded', 'false');
        highlightIndex = -1;
    };

    // Keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
        if (!isOpen && e.key === 'ArrowDown') {
            openDropdown();
            if (inputEl.value) {
                debouncedFetch(inputEl.value);
            }
            return;
        }

        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                highlightIndex = Math.min(highlightIndex + 1, options.length - 1);
                updateHighlight();
                break;
            case 'ArrowUp':
                e.preventDefault();
                highlightIndex = Math.max(highlightIndex - 1, 0);
                updateHighlight();
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightIndex >= 0 && highlightIndex < options.length) {
                    selectOption(options[highlightIndex]);
                }
                break;
            case 'Escape':
                closeDropdown();
                break;
        }
    };

    const updateHighlight = () => {
        const items = dropdown.querySelectorAll('.ev-search-option');
        items.forEach((item, i) => {
            item.classList.toggle('ev-search-option--highlighted', i === highlightIndex);
        });
    };

    // Event handlers
    const handleInput = () => {
        if (!isOpen) openDropdown();
        debouncedFetch(inputEl.value);
    };

    const handleFocus = () => {
        if (inputEl.value || options.length > 0) {
            openDropdown();
        }
    };

    const handleBlur = (e: FocusEvent) => {
        // Delay to allow click on dropdown
        setTimeout(() => {
            if (!container.contains(document.activeElement)) {
                closeDropdown();
            }
        }, 150);
    };

    // Wire up events
    inputEl.addEventListener('input', handleInput);
    inputEl.addEventListener('focus', handleFocus);
    inputEl.addEventListener('blur', handleBlur);
    inputEl.addEventListener('keydown', handleKeyDown);

    // Build container
    container.appendChild(selectedContainer);
    container.appendChild(inputEl);
    container.appendChild(dropdown);

    // Initial render
    renderSelected();

    return {
        element: container,
        getValue: () => {
            if (field.multiple) {
                return selectedItems;
            }
            return selectedItems.length > 0 ? selectedItems[0] : null;
        },
        setValue: (value: unknown) => {
            if (value == null) {
                selectedItems = [];
            } else if (Array.isArray(value)) {
                selectedItems = value as SelectOption[];
            } else {
                selectedItems = [value as SelectOption];
            }
            renderSelected();
        },
        validate: async () => true,
        focus: () => inputEl.focus(),
        dispose: () => {
            abortController?.abort();
            inputEl.removeEventListener('input', handleInput);
            inputEl.removeEventListener('focus', handleFocus);
            inputEl.removeEventListener('blur', handleBlur);
            inputEl.removeEventListener('keydown', handleKeyDown);
        },
    };
}
